import { FC, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { MileageHistoryItemResponseDto } from '@/apis/rest/generated/types/mileageHistoryItemResponseDto'
import { TextLegacy } from '@/components/common'
import { NewRow } from '@/components/common/layouts/NewRow'
import { ListItemUserMileageRecord } from '@/components/domains/listItems'

interface ListUserMileageRecordsProps {
  histories: MileageHistoryItemResponseDto[]
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => void
}

const ListUserMileageRecords: FC<ListUserMileageRecordsProps> = ({
  histories,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}) => {
  // 🌟 1. Props 최신화 거울 (이전과 동일하게 유지하여 중복 호출 방지)
  const isFetchingRef = useRef(isFetchingNextPage)
  const hasNextPageRef = useRef(hasNextPage)
  const onLoadMoreRef = useRef(onLoadMore)

  useEffect(() => {
    isFetchingRef.current = isFetchingNextPage
    hasNextPageRef.current = hasNextPage
    onLoadMoreRef.current = onLoadMore
  }, [isFetchingNextPage, hasNextPage, onLoadMore])

  // 🌟 2. Observer 객체를 담아둘 Ref
  const observer = useRef<IntersectionObserver | null>(null)

  // 🌟 3. 핵심: DOM 요소가 그려질 때 실행되는 Callback Ref
  const bottomBoundaryRef = useCallback((node: HTMLDivElement | null) => {
    // 기존에 관찰 중이던 게 있으면 먼저 해제
    if (observer.current) {
      observer.current.disconnect()
    }

    // 바닥 타겟(node)이 실제로 화면에 렌더링 된 경우에만 Observer 생성 및 부착
    if (node) {
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPageRef.current && !isFetchingRef.current && onLoadMoreRef.current) {
            isFetchingRef.current = true
            onLoadMoreRef.current()
          }
        },
        { threshold: 0, rootMargin: '0px 0px 50px 0px' }
      )
      observer.current.observe(node)
    }
  }, []) // 의존성 배열을 비워두어 무한 재생성(중복 호출) 버그 원천 차단

  if (!histories || histories.length === 0) {
    return (
      <EmptyTextRelativeDiv>
        <EmptyTextDiv>
          <NewRow justify="center" align="center">
            <TextLegacy>적립금 내역이 없습니다.</TextLegacy>
          </NewRow>
        </EmptyTextDiv>
      </EmptyTextRelativeDiv>
    )
  }

  return (
    <>
      {histories.map((item, index) => (
        <ListItemUserMileageRecord key={index} mileage={item} />
      ))}

      {/* 🌟 4. ref에 아까 만든 bottomBoundaryRef를 연결해 줍니다. */}
      <div ref={bottomBoundaryRef} style={{ height: '20px' }} />

      {isFetchingNextPage && (
        <NewRow justify="center" style={{ padding: '20px 0' }}>
          <TextLegacy color="gray400">데이터를 불러오는 중...</TextLegacy>
        </NewRow>
      )}
    </>
  )
}

const EmptyTextRelativeDiv = styled.div`
  width: 100%;
  height: 55vh;
`

const EmptyTextDiv = styled.div`
  position: absolute;
  width: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

export default ListUserMileageRecords
